import { createTheme } from '@mui/material/styles';

// =============================================================================
// Lumo – Modern Youth-Focused Digital Bank
// Bright, bold, playful. Coral + electric teal on warm cream.
// Extra-rounded shapes, chunky typography, high energy.
// Nothing subtle — this is the anti-Berchtold.
// =============================================================================

const lumoTheme = createTheme({
  palette: {
    mode: 'light',
    logo: 'light',
    bank: {
      name: 'Lumo',
      logo: '/bank-logos/lumo.svg',
    },
    primary: {
      main: '#ff6b6b',
      dark: '#e85555',
      light: '#ff8787',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00cec9',
      dark: '#00a8a4',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fff8f0',
      paper: '#fff0e8',
      surface: '#fff0e8',
      gradient: 'linear-gradient(160deg, #fff0e8 0%, #fff8f0 40%, #f0fffe 100%)',
    },
    text: {
      primary: '#2d3436',
      secondary: '#636e72',
      disabled: '#b2bec3',
      hint: '#95a5a6',
      dark: '#1a1a2e',
      body: '#2d3436',
      heading: '#2d3436',
    },
    success: {
      main: '#00b894',
      light: '#e6f9f3',
      dark: '#00806a',
      border: '#b8e8dc',
    },
    error: {
      main: '#ff6b6b',
      light: '#ffe8e8',
    },
    warning: {
      main: '#fdcb6e',
    },
    grey: {
      50: '#fff8f0',
      100: '#fff0e8',
      200: '#ffe4d6',
      300: '#f0ddd0',
      400: '#c8b8aa',
      500: '#9e9e9e',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#2d3436',
    },
    divider: 'rgba(255, 107, 107, 0.10)',
    action: {
      hover: 'rgba(255, 107, 107, 0.08)',
    },
    brand: {
      cyan: '#ff6b6b',
      cyanHover: '#e85555',
      cyanLight: '#ff8787',
      navy: '#fff8f0',
      navyLight: '#ffffff',
      navyMid: '#fff0e8',
      dialogBg: '#ffffff',
    },
    border: {
      light: 'rgba(255, 107, 107, 0.12)',
      medium: 'rgba(255, 107, 107, 0.18)',
      dark: 'rgba(255, 107, 107, 0.25)',
      input: 'rgba(45, 52, 54, 0.20)',
      muted: 'rgba(255, 107, 107, 0.08)',
      tableCell: 'rgba(255, 107, 107, 0.08)',
      onDark: 'rgba(255, 107, 107, 0.06)',
      onDarkAlt: 'rgba(255, 107, 107, 0.10)',
    },
    voice: {
      initBg1: '#fff5f5',
      initBg2: '#ffe8e8',
      initBg3: '#ffd4d4',
      thinkBg1: '#fffde8',
      thinkBg2: '#fff8c4',
      thinkBg3: '#fff3a0',
      speakBg1: '#e6f9f3',
      speakBg2: '#c8f0e4',
      speakBg3: '#a0e6d2',
      overlayBg: 'linear-gradient(180deg, #fff8f0 0%, #fff0e8 50%, #fff8f0 100%)',
    },
    gradient: {
      avatar: 'linear-gradient(135deg, #ff6b6b 0%, #fdcb6e 100%)',
      voiceButton: 'linear-gradient(135deg, #fff0e8 0%, #ffe4d6 100%)',
      voiceTooltip: 'linear-gradient(135deg, #ff6b6b 0%, #00cec9 100%)',
    },
  },
  typography: {
    fontFamily: "'Nunito', sans-serif",
    h1: { fontSize: '48px', fontWeight: 900, letterSpacing: '-0.03em' },
    h2: { fontSize: '22px', fontWeight: 400, letterSpacing: '0.02em' },
    h3: { fontSize: '20px', fontWeight: 700 },
    h4: { fontSize: '18px', fontWeight: 700 },
    h5: { fontSize: '16px', fontWeight: 700 },
    h6: { fontSize: '14px', fontWeight: 700 },
    body1: { fontSize: '14px', fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: '13px', fontWeight: 400, lineHeight: 1.5 },
    caption: { fontSize: '12px', fontWeight: 500 },
    overline: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' },
    button: { fontSize: '14px', fontWeight: 700, textTransform: 'none' },
  },
  shape: {
    borderRadius: 20,
    radiusSmall: 10,
    radiusMedium: 14,
    radiusLarge: 22,
    radiusXl: 26,
    radiusXxl: 32,
    radiusPill: 50,
  },
  shadows: [
    'none',
    '0 2px 4px rgba(255, 107, 107, 0.08)',
    '0 4px 20px rgba(255, 107, 107, 0.10)',
    '0 2px 8px rgba(255, 107, 107, 0.10)',
    '0 6px 20px rgba(0, 0, 0, 0.08)',
    '0 8px 30px rgba(255, 107, 107, 0.15)',
    '0 2px 10px rgba(0, 0, 0, 0.06)',
    '0 4px 20px rgba(255, 107, 107, 0.18)',
    '0 8px 30px rgba(255, 107, 107, 0.22)',
    '0 0 50px rgba(255, 107, 107, 0.12)',
    '0 0 80px rgba(255, 107, 107, 0.10)',
    'inset 0 0 20px rgba(255, 107, 107, 0.06)',
    ...Array(13).fill('none'),
  ],
  spacing: 8,
  breakpoints: {
    values: { xs: 0, sm: 600, md: 768, lg: 1068, xl: 1536 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          fontFamily: "'Nunito', sans-serif",
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 50, textTransform: 'none', fontFamily: "'Nunito', sans-serif", fontWeight: 700 },
        containedPrimary: {
          backgroundColor: '#ff6b6b',
          boxShadow: '0 4px 14px rgba(255, 107, 107, 0.3)',
          '&:hover': { backgroundColor: '#e85555', boxShadow: '0 6px 20px rgba(255, 107, 107, 0.4)' },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          background: 'rgba(255, 255, 255, 0.7)',
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 107, 107, 0.12)',
          borderRadius: '20px !important',
          boxShadow: '0 4px 20px rgba(255, 107, 107, 0.06)',
          transition: 'all 0.3s ease',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.9)',
            borderColor: 'rgba(0, 206, 201, 0.35)',
            boxShadow: '0 8px 30px rgba(255, 107, 107, 0.12)',
            transform: 'translateY(-1px)',
          },
          '&.Mui-expanded': {
            background: '#ffffff',
            borderColor: 'rgba(255, 107, 107, 0.30)',
            boxShadow: '0 8px 30px rgba(255, 107, 107, 0.12)',
          },
          '&:before': { display: 'none' },
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: { fontFamily: "'Nunito', sans-serif" },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: '#ffffff',
          backgroundImage: 'none',
          borderRadius: 24,
          border: '2px solid rgba(255, 107, 107, 0.15)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          background: '#ffffff',
          backgroundImage: 'none',
          borderRadius: 16,
          border: '1px solid rgba(255, 107, 107, 0.10)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          background: '#ffffff',
          backgroundImage: 'none',
          borderRadius: 16,
          border: '1px solid rgba(255, 107, 107, 0.10)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#ff6b6b', height: 3, borderRadius: 2 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: "'Nunito', sans-serif",
          fontWeight: 700,
          '&.Mui-selected': { color: '#ff6b6b' },
        },
      },
    },
  },
});

export default lumoTheme;
